import { useState, useEffect, useMemo } from "react";
import { useAuth, API } from "@/App";
import axios from "axios";
import { Loader2, ChevronLeft, ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";

const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

function scoreToColor(score) {
  if (score >= 8) return "bg-green-500/90";
  if (score >= 6) return "bg-green-400/70";
  if (score >= 5) return "bg-amber-400/70";
  if (score >= 3) return "bg-orange-400/70";
  return "bg-red-400/70";
}

function scoreToLabel(score) {
  if (score >= 8) return "Great";
  if (score >= 6) return "Good";
  if (score >= 5) return "Okay";
  if (score >= 3) return "Low";
  return "Rough";
}

export function MoodHeatmap() {
  const { token } = useAuth();
  const [cells, setCells] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hoveredCell, setHoveredCell] = useState(null);
  const [monthOffset, setMonthOffset] = useState(0);
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    fetchHeatmap();
  }, []);

  const fetchHeatmap = async () => {
    try {
      const { data } = await axios.get(`${API}/mood/heatmap?days=90`, { headers });
      setCells(data.cells || []);
    } catch { /* silent */ }
    finally { setLoading(false); }
  };

  const cellMap = useMemo(() => {
    const m = {};
    cells.forEach(c => { m[c.date] = c; });
    return m;
  }, [cells]);

  const viewMonth = useMemo(() => {
    const d = new Date();
    d.setMonth(d.getMonth() + monthOffset);
    return d;
  }, [monthOffset]);

  const monthLabel = viewMonth.toLocaleDateString("en", { month: "long", year: "numeric" });

  const calendarDays = useMemo(() => {
    const year = viewMonth.getFullYear();
    const month = viewMonth.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);

    // getDay: 0=Sun. Convert to Mon=0
    let startPad = (firstDay.getDay() + 6) % 7;
    const days = [];

    for (let i = 0; i < startPad; i++) {
      days.push(null);
    }
    for (let d = 1; d <= lastDay.getDate(); d++) {
      const dateStr = `${year}-${String(month + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
      const today = new Date();
      const isToday = dateStr === `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;
      const isFuture = new Date(dateStr) > today;
      days.push({ date: dateStr, day: d, cell: cellMap[dateStr] || null, isToday, isFuture });
    }
    return days;
  }, [viewMonth, cellMap]);

  if (loading) {
    return (
      <div className="card-omni p-12 flex flex-col items-center justify-center gap-3">
        <Loader2 className="w-6 h-6 animate-spin text-primary" />
        <p className="text-sm text-muted-foreground">Loading heatmap...</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="card-omni p-5 sm:p-7">
        {/* Month navigation */}
        <div className="flex items-center justify-between mb-5">
          <button
            data-testid="heatmap-prev-month"
            onClick={() => setMonthOffset(m => m - 1)}
            disabled={monthOffset <= -2}
            className="w-8 h-8 rounded-full flex items-center justify-center hover:bg-muted/50 transition-colors disabled:opacity-30"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <h3 data-testid="heatmap-month-label" className="font-heading text-base sm:text-lg font-medium">{monthLabel}</h3>
          <button
            data-testid="heatmap-next-month"
            onClick={() => setMonthOffset(m => m + 1)}
            disabled={monthOffset >= 0}
            className="w-8 h-8 rounded-full flex items-center justify-center hover:bg-muted/50 transition-colors disabled:opacity-30"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>

        {/* Weekday headers */}
        <div className="grid grid-cols-7 gap-1 sm:gap-1.5 mb-1">
          {WEEKDAYS.map(d => (
            <div key={d} className="text-center text-[10px] sm:text-xs text-muted-foreground font-medium py-1">{d}</div>
          ))}
        </div>

        {/* Calendar grid */}
        <div className="grid grid-cols-7 gap-1 sm:gap-1.5">
          {calendarDays.map((entry, i) => {
            if (!entry) return <div key={`pad-${i}`} className="aspect-square" />;
            const { date, day, cell, isToday, isFuture } = entry;
            const hasData = !!cell;
            return (
              <div
                key={date}
                data-testid={`heatmap-cell-${date}`}
                className="relative"
                onMouseEnter={() => hasData && setHoveredCell(date)}
                onMouseLeave={() => setHoveredCell(null)}
              >
                <div
                  className={cn(
                    "aspect-square rounded-lg sm:rounded-xl flex items-center justify-center text-[11px] sm:text-xs font-medium transition-all cursor-default",
                    isFuture && "opacity-30",
                    isToday && "ring-2 ring-primary ring-offset-1 ring-offset-background",
                    hasData ? `${scoreToColor(cell.score)} text-white` : "bg-muted/40 text-muted-foreground"
                  )}
                >
                  {day}
                </div>
                {/* Tooltip on hover */}
                {hoveredCell === date && hasData && (
                  <div className="absolute z-30 bottom-full left-1/2 -translate-x-1/2 mb-1.5 bg-card border border-border/50 rounded-xl shadow-lg px-3 py-2 min-w-[120px] text-center animate-in fade-in zoom-in-95 duration-100">
                    <p className="text-xs font-medium">{scoreToLabel(cell.score)} ({cell.score}/10)</p>
                    <p className="text-[10px] text-muted-foreground capitalize">{cell.top_emotion}</p>
                    <p className="text-[10px] text-muted-foreground">{cell.entries} {cell.entries === 1 ? "entry" : "entries"}</p>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Legend */}
      <div className="flex items-center justify-center gap-2 sm:gap-3 flex-wrap">
        <span className="text-[10px] sm:text-xs text-muted-foreground">Less</span>
        {[
          { color: "bg-red-400/70", label: "1-2" },
          { color: "bg-orange-400/70", label: "3-4" },
          { color: "bg-amber-400/70", label: "5" },
          { color: "bg-green-400/70", label: "6-7" },
          { color: "bg-green-500/90", label: "8-10" },
        ].map(l => (
          <div key={l.label} className="flex items-center gap-1">
            <div className={`w-3 h-3 sm:w-3.5 sm:h-3.5 rounded ${l.color}`} />
            <span className="text-[10px] sm:text-xs text-muted-foreground">{l.label}</span>
          </div>
        ))}
        <span className="text-[10px] sm:text-xs text-muted-foreground">More</span>
      </div>

      {cells.length === 0 && (
        <div className="card-omni p-8 text-center">
          <p className="text-muted-foreground mb-1">No mood data yet</p>
          <p className="text-sm text-muted-foreground">Start logging your mood to see patterns emerge on the heatmap.</p>
        </div>
      )}
    </div>
  );
}
